/**
 * Pure per-sport stat selection for board rows (forecast areas spec, sections 5.3 and 10.2).
 *
 * The stat family comes from `rowStatsFor()` in `lib/areas/row-model.ts`, the one rule shared
 * with area pages, so a board row and an area row show the same facts: swell only on surf and
 * bodyboarding rows, snow on the ski boards, wind on the wind and foil boards, water on diving,
 * open water and the generic paddle board, temperature and wind on MTB, and wind plus venue on
 * sailing. No swell on MTB, wind or diving rows.
 */
import type { LeaderboardEntry } from "@/lib/conditions/board";
import { rowStatsFor, type AreaRowStats } from "@/lib/areas/row-model";

/** The board categories `LeaderboardTable` knows, without importing the client component. */
type BoardCategory =
  | "ski" | "surf" | "wind" | "foil" | "downwind-foil" | "wing-foil" | "diving"
  | "sup" | "sup-touring" | "sup-surf" | "sup-downwind" | "swim" | "mtb" | "touring"
  | "sailing" | "bodyboarding";

type BoardSkiSport = "piste" | "offpiste" | "touring";

export type BoardRowStatItem = Readonly<{ key: string; value: string; label: string }>;

const NAMED_PROFILE_CATEGORIES = new Set(["sup-touring", "sup-surf", "sup-downwind"]);

/**
 * The area board sport for a board category, or null for the named paddle profiles (their
 * boards keep their own presentation and never join the area programme).
 */
export function boardStatsSport(category: BoardCategory, skiSport: BoardSkiSport = "piste"): string | null {
  if (NAMED_PROFILE_CATEGORIES.has(category)) return null;
  switch (category) {
    case "ski": return skiSport === "offpiste" ? "off-piste" : skiSport === "touring" ? "touring" : "piste";
    case "swim": return "swimming";
    default: return category;
  }
}

function fixed(value: number | null, digits: number, unit: string): string {
  return value === null ? "–" : `${value.toFixed(digits)}${unit}`;
}

const VENUE_LABELS: Readonly<Record<string, string>> = {
  inland: "Inland",
  estuary: "Estuary",
  coastal_open: "Coastal",
};

const WIND_RELATION_LABELS: Readonly<Record<string, string>> = {
  offshore: "offshore",
  "cross-shore": "cross-shore",
  onshore: "onshore",
};

/** Display items for a stats record, in reading order. */
export function statItems(stats: AreaRowStats): readonly BoardRowStatItem[] {
  switch (stats.kind) {
    case "surf":
      return [
        { key: "swell", value: fixed(stats.swellM, 1, " m"), label: "swell" },
        { key: "period", value: fixed(stats.periodS, 0, " s"), label: "period" },
        {
          key: "wind",
          value: fixed(stats.windKmh, 0, " km/h"),
          label: stats.windRelation ? `wind, ${WIND_RELATION_LABELS[stats.windRelation]}` : "wind",
        },
        { key: "water", value: fixed(stats.waterC, 0, "°"), label: "water" },
      ];
    case "snow":
      return [
        { key: "new-snow", value: fixed(stats.newSnow48hCm, 0, " cm"), label: "48h" },
        { key: "base", value: stats.depthCm !== null && stats.depthCm > 0 ? `${stats.depthCm.toFixed(0)} cm` : "–", label: "base" },
        { key: "min-temp", value: fixed(stats.tempMinC, 0, "°"), label: "min" },
      ];
    case "wind":
      return [
        { key: "wind", value: fixed(stats.windKmh, 0, " km/h"), label: "wind" },
        { key: "water", value: fixed(stats.waterC, 0, "°"), label: "water" },
      ];
    case "water":
      return [
        { key: "water", value: fixed(stats.waterC, 0, "°"), label: "water" },
        { key: "swell", value: fixed(stats.swellM, 1, " m"), label: "swell" },
      ];
    case "trail":
      return [
        { key: "min-temp", value: fixed(stats.tempMinC, 0, "°"), label: "min" },
        { key: "wind", value: fixed(stats.windKmh, 0, " km/h"), label: "wind" },
      ];
    case "sailing": {
      const items: BoardRowStatItem[] = [{ key: "wind", value: fixed(stats.windKmh, 0, " km/h"), label: "wind" }];
      if (stats.venueType) {
        items.push({ key: "venue", value: VENUE_LABELS[stats.venueType] ?? stats.venueType, label: "venue" });
      }
      return items;
    }
  }
}

/** Pure: the stat items a board row shows for its board sport. */
export function boardRowStatItems(sport: string, entry: Partial<LeaderboardEntry>): readonly BoardRowStatItem[] {
  return statItems(rowStatsFor(sport, entry));
}
